import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../finance/auth/AuthContext';
import { normalizeRole } from '../../finance/auth/permissions';
import companyLogoText from '../../assets/logo-text.png';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';
import {
  FiHome, FiUsers, FiCalendar, FiBriefcase, FiDollarSign,
  FiChevronDown, FiChevronRight, FiLogOut, FiX, FiLayers, FiCheckSquare,
  FiCreditCard, FiFileText, FiShield, FiUser, FiTrendingUp, FiBookOpen, FiChevronsRight, FiChevronsLeft
} from 'react-icons/fi';
import logoIcon from '../../assets/logo-icon.png';

// Defined outside to prevent unnecessary remounting
const NavItem = ({ path, icon: Icon, label, onClick, isCollapsed }) => (
  <li>
    <NavLink
      to={path}
      onClick={onClick}
      title={isCollapsed ? label : ''}
      className={({ isActive }) =>
        `group flex items-center px-4 py-2.5 mx-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
          ? 'bg-blue-600 text-white shadow-md shadow-blue-900/20'
          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        } ${isCollapsed ? 'justify-center px-0' : 'gap-3'}`
      }
    >
      <Icon className="text-lg shrink-0" />
      {!isCollapsed && <span className="truncate">{label}</span>}
    </NavLink>
  </li>
);

const NavSection = ({ sectionKey, icon: Icon, title, children, isVisible, expandedSections, toggleSection, isCollapsed, toggleCollapse }) => {
  if (!isVisible) return null;
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { isCollapsed });
    }
    return child;
  });
  return (
    <div className="mb-2">
      {!isCollapsed ? (
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between px-6 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase hover:text-slate-300 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon className="text-sm" />
            <span>{title}</span>
          </div>
          {expandedSections[sectionKey] ? <FiChevronDown /> : <FiChevronRight />}
        </button>
      ) : (
        <button
          onClick={() => {
            toggleCollapse();
            toggleSection(sectionKey);
          }}
          className="w-full flex items-center justify-center py-4 text-slate-500 hover:text-white transition-colors"
          title={title}
        >
          <Icon className="text-xl" />
        </button>
      )}
      <div className={`overflow-hidden transition-all duration-300 ${!isCollapsed && expandedSections[sectionKey] ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <ul className="space-y-1 mt-1">
          {childrenWithProps}
        </ul>
      </div>
    </div>
  );
};

const Sidebar = ({ isOpen, toggleSidebar, isCollapsed, toggleCollapse }) => {
  const { role, depts, logout, username } = useAuth();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState(() => {
    return {
      myspace: true,
      sales: false,
      it: false,
      hr: false,
      finance: false,
      admin: false
    };
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = prev[section];
      if (isCurrentlyOpen) {
        return { ...prev, [section]: false };
      }

      // Accordion behavior: close all others, open the selected one
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      newState[section] = true;
      return newState;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const hasDept = (deptName) => depts?.some(d => String(d).toUpperCase() === deptName.toUpperCase());
  const normalizedRole = normalizeRole(role);
  const isSuperadmin = normalizedRole === 'SUPERADMIN' || normalizedRole === 'SUPER_ADMIN' || String(username || '').toLowerCase() === 'superadmin';
  const isAdminRole = normalizedRole === 'ADMIN';
  const isAnyAdmin = isSuperadmin || isAdminRole;
  const [hasSubordinates, setHasSubordinates] = useState(false);

  useEffect(() => {
    if (isAnyAdmin) {
      setHasSubordinates(true);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    fetch(`${baseUrl}/leave/subordinates`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const list = data?.data ?? data ?? [];
        if (Array.isArray(list) && list.length > 0) {
          setHasSubordinates(true);
        }
      })
      .catch(() => { });
  }, [isAnyAdmin]);

  const hasHR = isAnyAdmin && (isSuperadmin || hasDept('HR'));
  const hasFinance = isAnyAdmin && (isSuperadmin || hasDept('FINANCE') || hasDept('PROCUREMENT') || hasDept('PAYROLL'));
  const hasIT = isSuperadmin || hasDept('IT') || hasDept('TECH') || hasDept('DEVELOPMENT') || hasDept('DEV') || hasDept('ENGINEERING') || hasDept('SOFTWARE');
  const hasSales = isSuperadmin || hasDept('SALES');

  const navSectionProps = { expandedSections, toggleSection, isCollapsed, toggleCollapse };

  const handleNavClick = (e) => {
    const isLink = e.target.closest('a');
    if (isLink && isOpen) {
      toggleSidebar();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen flex flex-col bg-slate-900 text-white 
        transition-all duration-300 ease-in-out border-r border-slate-800 shadow-2xl
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'w-[280px] sm:w-72 lg:w-20' : 'w-[280px] sm:w-72'}
      `}>
        {/* Logo / Header Area */}
        <div className={`relative flex items-center ${isCollapsed ? 'h-[4.25rem] justify-center px-2 lg:px-0' : 'h-[4.25rem] justify-center pl-4 pr-11'} border-b border-slate-800/60 bg-slate-900/60 backdrop-blur-xs shrink-0 transition-all duration-300`}>
          {/* Expanded Logo: always shown on mobile (< lg), and shown on desktop when !isCollapsed */}
          <div className={`flex-1 flex items-center justify-center min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
            <NavLink to="/EmpDashboard" className="flex items-center justify-center min-w-0 group" title="Orakris Med Solutions">
              <img 
                src={companyLogoText} 
                alt="Orakris Logo" 
                className="h-[50px] sm:h-[52px] w-auto max-w-[215px] object-contain filter brightness-150 contrast-115 drop-shadow-[0_2px_10px_rgba(56,189,248,0.2)] group-hover:opacity-100 transition-all" 
              />
            </NavLink>
          </div>

          {/* Desktop Collapse / Expand Controls */}
          {!isCollapsed && (
            <button 
              onClick={toggleCollapse} 
              className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors" 
              title="Collapse Sidebar"
            >
              <FiChevronsLeft className="text-xl" />
            </button>
          )}

          {isCollapsed && (
            <button 
              onClick={toggleCollapse} 
              className="hidden lg:flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors" 
              title="Expand Sidebar"
            >
              <FiChevronsRight className="text-2xl" />
            </button>
          )}

          {/* Mobile Close Button */}
          <button 
            onClick={toggleSidebar} 
            className="lg:hidden absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1"
          >
            <FiX className="text-2xl" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar" onClick={handleNavClick}>

          <NavSection sectionKey="myspace" icon={FiUser} title="My Space" isVisible={true} {...navSectionProps}>
            <NavItem path="/onboarding" icon={FiFileText} label="Onboarding Docs" />
            <NavItem path="/my-space/tasks" icon={FiCheckSquare} label="My Work & Tasks" />
            <NavItem path="/leaves-wfh" icon={FiCalendar} label="Leaves" />
            {(isAnyAdmin || hasSubordinates) && (
              <NavItem path="/manager/leave-approvals" icon={FiUsers} label="Team Approvals" />
            )}
          </NavSection>

          <NavSection sectionKey="sales" icon={FiTrendingUp} title="Sales" isVisible={hasSales} {...navSectionProps}>
            <NavItem path="/sales/dashboard" icon={FiHome} label="Sales Dashboard" />
            <NavItem path="/sales/leads" icon={FiBriefcase} label="Leads" />
            <NavItem path="/sales/tutorials" icon={FiBookOpen} label="Tutorials" />
          </NavSection>

          <NavSection sectionKey="it" icon={FiLayers} title="Tech Dept" isVisible={hasIT} {...navSectionProps}>
            <NavItem path="/it/dashboard" icon={FiHome} label="Tech Dashboard" />
            <NavItem path="/EmpProducts" icon={FiLayers} label="Products" />
            <NavItem path="/TasksView" icon={FiCheckSquare} label="Tasks" />
            <NavItem path="/tech/docs" icon={FiBookOpen} label="API Documentation" />
          </NavSection>

          <NavSection sectionKey="hr" icon={FiUsers} title="Human Resources" isVisible={hasHR} {...navSectionProps}>
            <NavItem path="/hr/teams" icon={FiUsers} label="Teams & Directory" />
            <NavItem path="/hr/leaves" icon={FiCalendar} label="Leave Management" />
            <NavItem path="/hr/leave-approvals" icon={FiCheckSquare} label="Leaves & WFH Approvals" />
            <NavItem path="/hr/attendance" icon={FiCalendar} label="Attendance" />
            <NavItem path="/hr/team-presence" icon={FiUsers} label="Team Presence" />
          </NavSection>

          <NavSection sectionKey="finance" icon={FiDollarSign} title="Finance Suite" isVisible={hasFinance} {...navSectionProps}>
            <NavItem path="/finance/dashboard" icon={FiHome} label="Finance Dashboard" />
            <NavItem path="/finance/expenses" icon={FiCreditCard} label="Expenses" />
            {/*  <NavItem path="/finance/reimbursements" icon={FiTrendingUp} label="Reimbursements" />*/}
            <NavItem path="/finance/invoices" icon={FiFileText} label="Invoices" />
            <NavItem path="/finance/payments" icon={FiDollarSign} label="Payments" />
            <NavItem path="/finance/assets" icon={FiBriefcase} label="Assets" />
            {/* <NavItem path="/finance/procurement" icon={FiTarget} label="Procurement" />*/}
            <NavItem path="/finance/payroll/manage" icon={FiDollarSign} label="Payroll Admin" />
            <NavItem path="/finance/approvals" icon={FiCheckSquare} label="Approvals Admin" />
          </NavSection>

          <NavSection sectionKey="admin" icon={FiShield} title="Administration" isVisible={isSuperadmin} {...navSectionProps}>
            <NavItem path="/admin/users" icon={FiUsers} label="Manage Users" />
            <NavItem path="/admin/leaves" icon={FiCalendar} label="Leave Management" />
            <NavItem path="/admin/holidays" icon={FiCalendar} label="Manage Holidays" />
            <NavItem path="/admin/onboarding-docs" icon={FiFileText} label="Manage Onboarding Docs" />
          </NavSection>

        </nav>

        {/* User Profile Footer */}
        <div className={`border-t border-slate-800 p-4 bg-slate-900 shrink-0 ${isCollapsed ? 'px-2' : ''}`}>
          {!isCollapsed && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold shadow-inner shrink-0">
                {username ? username.substring(0, 2).toUpperCase() : 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{username || 'User'}</div>
                <div className="text-xs text-slate-400 truncate capitalize">{role?.replace('ROLE_', '').toLowerCase() || 'Employee'}</div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sign Out' : ''}
            className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-colors border border-slate-700 hover:border-red-500/30 ${isCollapsed ? 'px-0' : 'px-4'}`}
          >
            <FiLogOut className="text-lg shrink-0" /> {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

